// apps/web/src/lib/haptics.ts
// Haptic feedback wrappers. In the iOS app the Capacitor Haptics plugin fires
// real Taptic Engine feedback; anywhere it's unavailable (desktop web, app
// builds without the plugin) every call is a silent no-op — feedback must
// never break a flow.
type HapticsModule = typeof import("@capacitor/haptics");

let mod: Promise<HapticsModule | null> | null = null;
function load(): Promise<HapticsModule | null> {
  if (!mod) mod = import("@capacitor/haptics").catch(() => null);
  return mod;
}

const OFF_KEY = "caloly.haptics_off";

/** Global kill-switch (More → Preferences). Checked on every fire. */
export function hapticsDisabled(): boolean {
  try { return localStorage.getItem(OFF_KEY) === "1"; } catch { return false; }
}
export function setHapticsDisabled(off: boolean): void {
  try {
    if (off) localStorage.setItem(OFF_KEY, "1");
    else localStorage.removeItem(OFF_KEY);
  } catch { /* storage unavailable */ }
}

// NOTE: there is deliberately no haptic for going over the calorie target —
// see hapticEvents in lib/tokens.ts. Never add one.
function fire(fn: (m: HapticsModule) => Promise<unknown>): void {
  if (hapticsDisabled()) return;
  void load().then((m) => (m ? fn(m).catch(() => { /* no haptics available */ }) : undefined));
}

/** Light tick — taps on tappable things (glasses, toggles, steppers, tabs). */
export const hapticTap = () => fire((m) => m.Haptics.impact({ style: m.ImpactStyle.Light }));

/** Medium thump — the start of something deliberate (hold-to-commit). */
export const hapticImpact = () => fire((m) => m.Haptics.impact({ style: m.ImpactStyle.Medium }));

/** Success buzz — something was logged/saved. */
export const hapticSuccess = () => fire((m) => m.Haptics.notification({ type: m.NotificationType.Success }));

/** Warning buzz — something was deleted. */
export const hapticWarning = () => fire((m) => m.Haptics.notification({ type: m.NotificationType.Warning }));

/** Selection tick — moving between options (meal slot, date, stepper unit). */
export const hapticSelect = () =>
  fire(async (m) => {
    // The dedicated selection API gives the crisp UIPickerView tick.
    await m.Haptics.selectionStart();
    await m.Haptics.selectionChanged();
    await m.Haptics.selectionEnd();
  });

/** High-frequency scroll tick — wheels and rulers, fired per row/tick pass.
    A single impact call (not the 3-call selection dance) so the tap lands the
    instant the value crosses the pointer, even mid-fling. */
export const hapticTick = () => fire((m) => m.Haptics.impact({ style: m.ImpactStyle.Light }));
